/**
 * ManualTopicDialog — directive (no-AI) topic creation.
 *
 * Posts to /api/agent/content-plan/topics/manual which:
 *   - Validates audience binding (audienceFit[] OR appliesToAllAudiences with rationale)
 *   - Validates dataUsage.items[] non-empty
 *   - Validates themeAssignment ids exist in the catalog
 *   - Writes to projects/{pid}/topic_backlog/{topicId} with kind='user_manual'
 *
 * Used by the topic backlog page "+ Add manually" entry-point.
 */

import React, { useState } from 'react';
import {
  Stack,
  TextField,
  Alert,
  Typography,
  Box,
  Divider,
  Select,
  MenuItem,
  Chip,
  FormControl,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import {
  BaseDialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
} from '@modules/ui-components';
import { ContentPlanApiClient } from '@modules/content-plan/services/ContentPlanApiClient';
import { useThemeCatalog } from '@modules/content-plan/hooks/useThemeCatalog';
import {
  AudienceFitFormSection,
  AUDIENCE_FIT_FORM_EMPTY_VALUE,
  type AudienceFitFormSectionValue,
} from '@modules/content-plan/components/strategy/AudienceFitFormSection';

export interface ManualTopicDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  userId: string;
  authorId: string;
  onCreated?: (topicId: string) => void;
}

export const ManualTopicDialog: React.FC<ManualTopicDialogProps> = ({
  open,
  onClose,
  projectId,
  userId,
  authorId,
  onCreated,
}) => {
  const { themes } = useThemeCatalog(projectId);

  const [angle, setAngle] = useState('');
  const [keyMessage, setKeyMessage] = useState('');
  const [summary, setSummary] = useState('');
  const [themeIds, setThemeIds] = useState<string[]>([]);
  const [primaryPillarId, setPrimaryPillarId] = useState('');
  const [audienceForm, setAudienceForm] = useState<AudienceFitFormSectionValue>(AUDIENCE_FIT_FORM_EMPTY_VALUE);
  const [dataUsageRaw, setDataUsageRaw] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setAngle('');
      setKeyMessage('');
      setSummary('');
      setThemeIds([]);
      setPrimaryPillarId('');
      setAudienceForm(AUDIENCE_FIT_FORM_EMPTY_VALUE);
      setDataUsageRaw('');
      setError(null);
    }
  }, [open]);

  const validate = (): string | null => {
    if (!angle.trim()) return 'Angle is required.';
    const hasAudienceFit = audienceForm.audienceFit.length > 0;
    const universal = audienceForm.appliesToAllAudiences;
    if (!hasAudienceFit && !universal) {
      return 'Set audienceFit[] or mark "Applies to all audiences" with a rationale.';
    }
    if (hasAudienceFit && universal) {
      return '"Applies to all audiences" is mutually exclusive with per-audience fits.';
    }
    if (universal && !audienceForm.appliesToAllAudiencesRationale.trim()) {
      return 'Universal topics require a rationale.';
    }
    if (hasAudienceFit) {
      const missingRationale = audienceForm.audienceFit.find((f) => !(f.rationale ?? '').trim());
      if (missingRationale) return 'Every audience fit needs a rationale.';
    }
    const usageLines = dataUsageRaw
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (usageLines.length === 0) {
      return 'Provide at least one dataUsage source path (one per line).';
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const items = dataUsageRaw
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((sourcePath) => ({ sourcePath }));
      const themeAssignment = themeIds.map((themeId) => ({ themeId, weight: 1 / themeIds.length }));
      const audienceFit = audienceForm.audienceFit.map((f) => ({
        audienceBriefRef: f.audienceBriefRef!,
        fit: f.fit as 'strong' | 'medium' | 'weak',
        rationale: f.rationale,
      }));
      const client = new ContentPlanApiClient();
      const result = await client.createManualTopic({
        projectId,
        userId,
        authorId,
        angle: angle.trim(),
        keyMessage: keyMessage.trim() || undefined,
        summary: summary.trim() || undefined,
        themeAssignment: themeAssignment.length > 0 ? themeAssignment : undefined,
        primaryPillarId: primaryPillarId.trim() || undefined,
        audienceFit: audienceFit.length > 0 ? audienceFit : undefined,
        appliesToAllAudiences: audienceForm.appliesToAllAudiences || undefined,
        appliesToAllAudiencesRationale: audienceForm.appliesToAllAudiences
          ? audienceForm.appliesToAllAudiencesRationale
          : undefined,
        dataUsage: { items },
      });
      if (!result.success) throw new Error(result.error ?? 'Topic creation failed');
      onCreated?.(result.topicId);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      data-testid="manual-topic-dialog"
    >
      <DialogHeader title="Create topic manually" onClose={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ py: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Angle (hook)"
            value={angle}
            onChange={(e) => setAngle(e.target.value)}
            fullWidth
            required
            multiline
            minRows={2}
            inputProps={{ 'data-testid': 'manual-topic-angle' }}
          />
          <TextField
            label="Key message"
            value={keyMessage}
            onChange={(e) => setKeyMessage(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            inputProps={{ 'data-testid': 'manual-topic-key-message' }}
          />
          <TextField
            label="Summary"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            inputProps={{ 'data-testid': 'manual-topic-summary' }}
          />

          {/* Theme multi-select */}
          <FormControl fullWidth>
            <InputLabel id="manual-topic-themes-label">Themes</InputLabel>
            <Select
              labelId="manual-topic-themes-label"
              multiple
              value={themeIds}
              onChange={(e) => setThemeIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              input={<OutlinedInput label="Themes" />}
              inputProps={{ 'data-testid': 'manual-topic-themes' }}
              renderValue={(selected) => (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {(selected as string[]).map((id) => {
                    const theme = themes.find((t) => t.id === id);
                    return <Chip key={id} size="small" label={theme?.name ?? id} />;
                  })}
                </Stack>
              )}
            >
              {themes.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Weights are auto-split evenly across selected themes.
            </Typography>
          </FormControl>

          <TextField
            label="Primary pillar id (optional)"
            value={primaryPillarId}
            onChange={(e) => setPrimaryPillarId(e.target.value)}
            fullWidth
            inputProps={{ 'data-testid': 'manual-topic-pillar' }}
          />

          <Divider>Audience binding</Divider>

          <AudienceFitFormSection
            projectId={projectId}
            value={audienceForm}
            onChange={setAudienceForm}
          />

          <Divider>Data citations</Divider>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              At least one source path. One per line, e.g.
              <code style={{ marginLeft: 4 }}>@insight[an_x].audienceContentBriefs[aud_y]</code>
            </Typography>
            <TextField
              value={dataUsageRaw}
              onChange={(e) => setDataUsageRaw(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              placeholder="@insight[an_health].audienceContentBriefs[hc_vp_ops]"
              inputProps={{ 'data-testid': 'manual-topic-data-usage' }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogFooter
        secondaryAction={{
          label: 'Cancel',
          onClick: onClose,
          'data-testid': 'manual-topic-cancel',
        } as any}
        primaryAction={{
          label: submitting ? 'Saving…' : 'Create topic',
          onClick: handleSave,
          disabled: submitting,
          'data-testid': 'manual-topic-submit',
        } as any}
      />
    </BaseDialog>
  );
};

export default ManualTopicDialog;
